<script lang="ts" setup>
defineProps({
  borderless: {
    default: false,
    type: Boolean,
  },
  cards: {
    default: () => [],
    type: Array as PropType<StepCard[]>,
  },
  columns: {
    default: 1,
    type: Number,
  },
  disableComponentRightBox: {
    default: false,
    type: Boolean,
  },
  type: {
    default: undefined,
    type: String as PropType<StepType>,
  },
})

const { codingLanguages, getPreferredCodingLanguage } = useCoding()
const { locale, t } = useI18n()
const route = useRoute()
const slots = useSlots()

const activeCodingLanguage = ref<CodingLanguage>()
const rootRef = ref<HTMLElement>()

const colors: Record<StepType, UBadgeColor> = {
  'api-tokens': 'green',
  'code-examples': 'fuchsia',
  'how-it-works': 'indigo',
  'introduction': 'purple',
  'our-api-endpoint': 'indigo',
  'to-go-further': 'blue',
  'what-you-should-know': 'violet',
}

const availableCodeSlots = computed(() => {
  const options: USelectMenuOption[] = Object.keys(slots)
    .filter(key => key.startsWith('code-'))
    .map((key) => {
      const language = key.replace('code-', '') as CodingLanguage

      return {
        color: codingLanguages[language]?.color,
        icon: getIcon(language) || 'code',
        label: t(`coding.languages.${language}`),
        value: language,
      }
    })
    .sort((current, next) => {
      return current.label.localeCompare(next.label)
    })

  return options
})

watch(
  () => route.query.language,
  (language) => {
    setActiveCodingLanguage(language as CodingLanguage)
  },
)

function setActiveCodingLanguage(language?: CodingLanguage) {
  const preferredLanguage = getPreferredCodingLanguage()

  if (language) {
    activeCodingLanguage.value = language
  }
  else if (preferredLanguage) {
    activeCodingLanguage.value = preferredLanguage
  }
  else if (availableCodeSlots.value.length) {
    const hasJS = availableCodeSlots.value.some((option) => {
      return option.value === 'javascript'
    })

    activeCodingLanguage.value = hasJS ? 'javascript' : availableCodeSlots.value[0].value
  }
}

setActiveCodingLanguage()
</script>

<template>
  <div
    ref="rootRef"
    class="not-prose step relative grid grid-cols-1 gap-6 text-left outline-none"
    :class="{
      'md:grid-cols-2': columns === 2,
      'gradient-texture-light rounded-lg bg-gray-100 p-5 shadow ring-1 ring-gray-200 md:p-6 dark:bg-gray-900 dark:ring-gray-800': !borderless,
    }"
  >
    <div
      v-if="type || slots[`content-${locale}`] || slots['component-left-bottom'] || slots[`power-${locale}`] || cards.length"
      class="flex flex-col gap-4"
    >
      <UBadge
        v-if="type"
        class="self-start"
        :color="colors[type]"
        size="md"
        variant="soft"
      >
        {{ t(`badges.${type}`) }}
      </UBadge>

      <div
        v-if="slots[`content-${locale}`]"
        class="step__content flex-initial text-base text-gray-900 md:text-lg dark:text-gray-100"
      >
        <MDCSlot :name="`content-${locale}`" />
      </div>

      <div
        v-if="slots['component-left-bottom'] || slots[`power-${locale}`]"
        class="mt-1"
      >
        <BaseQuote v-if="slots[`power-${locale}`]">
          <MDCSlot :name="`power-${locale}`" />
        </BaseQuote>

        <MDCSlot name="component-left-bottom" />
      </div>

      <div
        v-else-if="cards.length"
        class="flex flex-col gap-4"
      >
        <div
          class="grid w-full gap-4"
          :class="{ 'grid-cols-1 md:grid-cols-2': columns === 1 }"
        >
          <StepCard
            v-for="(card, cardIndex) in cards"
            :key="cardIndex"
            :active-color="card.activeColor"
            class="size-full self-start"
            :content="card[`content-${locale}`]"
            :description="card[`description-${locale}`]"
            :logo="card.logo"
            :number="cardIndex + 1"
            :title="card[`title-${locale}`]"
            :url="card.url"
          />
        </div>
      </div>
    </div>

    <MDCSlot name="component-full" />

    <div
      v-if="slots['component-right']"
      class="relative"
      :class="{ 'rounded-lg bg-gray-100 p-4 shadow ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800': !disableComponentRightBox }"
    >
      <AnimationDotsPattern v-if="!disableComponentRightBox" />

      <div
        class="relative z-20 flex min-h-56 flex-col"
        :class="{ 'items-center justify-center': !disableComponentRightBox }"
      >
        <UBadge
          v-if="slots[`component-right-badge-${locale}`]"
          class="absolute left-0 top-0"
          size="lg"
          variant="soft"
        >
          <MDCSlot :name="`component-right-badge-${locale}`" />
        </UBadge>

        <MDCSlot name="component-right" />

        <BaseHighlighter
          v-if="slots[`component-right-highlight-${locale}`]"
          class="mt-6"
        >
          <MDCSlot :name="`component-right-highlight-${locale}`" />
        </BaseHighlighter>
      </div>
    </div>

    <div
      v-if="availableCodeSlots.length"
      class="group relative size-full rounded-lg bg-gray-900 text-left text-sm shadow ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800"
    >
      <div class="max-h-80 overflow-x-auto p-2 md:absolute md:inset-0 md:max-h-none md:p-4">
        <MDCSlot :name="`code-${activeCodingLanguage}`" />
      </div>
    </div>
  </div>
</template>

<style>
.step {
  .step__content {
    a:not(.ignore-step-style) {
      @apply underline font-bold hover:text-gray-700 dark:hover:text-gray-300;
    }

    code,
    pre {
      @apply text-base;
    }

    code:not(pre code) {
      @apply text-gray-900 px-1.5 py-0.5 rounded dark:text-gray-100 dark:bg-gray-900 bg-gray-100;
    }

    pre {
      @apply p-4 overflow-auto rounded-lg text-gray-900 ring-1 ring-gray-200 dark:ring-gray-800 dark:bg-gray-900 bg-gray-900;
    }

    li, ol, p, pre, ul {
      @apply mt-4;
    }

    li, p {
      @apply first-of-type:mt-0;
    }

    ul {
      @apply list-disc pl-3 md:pl-4;
    }

    ol {
      @apply list-decimal pl-3 md:pl-4;
    }
  }
}
</style>

<i18n lang="json">
{
  "en": {
    "badges": {
      "api-tokens": "API tokens",
      "code-examples": "Code examples",
      "how-it-works": "How it works",
      "introduction": "Introduction",
      "our-api-endpoint": "Our API endpoint",
      "to-go-further": "To go further",
      "use-cases": "How they use our API",
      "what-you-should-know": "What you should know"
    },
    "messages": {
      "learnMore": "Learn more",
      "tryIt": "Try it for yourself"
    },
    "step": "Step {step}"
  },
  "es": {
    "badges": {
      "api-tokens": "Tokens de API",
      "code-examples": "Ejemplos de código",
      "how-it-works": "Cómo funciona",
      "introduction": "Introducción",
      "our-api-endpoint": "Nuestro endpoint de API",
      "to-go-further": "Para ir más allá",
      "use-cases": "Cómo usan nuestra API",
      "what-you-should-know": "Lo que debes saber"
    },
    "messages": {
      "learnMore": "Aprender más",
      "tryIt": "Intenta por ti mismo"
    },
    "step": "Paso {step}"
  },
  "fr": {
    "badges": {
      "api-tokens": "Jetons API",
      "code-examples": "Exemples de code",
      "how-it-works": "Comment ça marche",
      "introduction": "Introduction",
      "our-api-endpoint": "Notre endpoint API",
      "to-go-further": "Pour aller plus loin",
      "use-cases": "Comment ils utilisent notre API",
      "what-you-should-know": "Ce que vous devriez savoir"
    },
    "messages": {
      "learnMore": "En savoir plus",
      "tryIt": "Essayez par vous-même"
    },
    "step": "Étape {step}"
  }
}
</i18n>
